<template lang="pug">
.bg-grey.w-full.p-40
  BriqPanel
    template(v-slot:header)
      h2.h2 Payments
      .actions
        BriqButton.ml-5(
          v-if="businessAllowsCashRefund" 
          @click="$bus.$emit('showRefundDialog')" 
          :disabled="disableCashRefund" 
          variant="secondary"
          data-test-id="payments-tab-cash-refund"
          ) Cash refund
        BriqButton.ml-5(
          v-if="businessAllowsAdminPayments" 
          @click="$bus.$emit('showAddPaymentDialog')" 
          :disabled="disableAddPayment" 
          variant="secondary" 
          data-test-id="payments-tab-add-payment-button"
          ) Add payment
    PaymentsTable(
      :payments="payments"
      :amountPaid="amountPaid"
      @refund="promptRefundPayment"
      )

  BriqDialog(v-show="showRefundDialog" @close="showRefundDialog = false")
    h2.mb-S Refund payment
    .mb-S Select the amount you would like to refund (max {{ formattedRefundMaxAmount }})?
    BriqPriceInput.mb-S(
      label="Amount"
      min="0"
      :max="refundMaxAmount"
      :currencySymbol="currencySymbol"
      type="number"
      v-model.number="refundAmount"
      required
      :error="refundError"
      data-test-id="payment-refund-input"
      )
    BriqTextarea.mb-S(label="Description" v-model="refundDescription")
    .flex.justify-center
      BriqButton.mr-XXS(variant="text" @click="showRefundDialog = false" :disabled="isPending") Cancel
      BriqButton.ml-XXS(@click="refundPayment" :disabled="disableDialogRefund") Refund
</template>

<script>
import { required, minValue, maxValue } from 'vuelidate/lib/validators'
import { clone, isEmpty } from 'ramda'
import { mapGetters, mapState } from 'vuex'
import { currencyFormatter } from '@/helpers/intlFormatter.js'
import PaymentsTable from './PaymentsTable.vue'
import Permission from '@v/models/UserPermissionEnum.js'
import BusinessFeature from '@v/models/BusinessFeatureEnum.js'
import BriqPriceInput from '@v/components/BriqPriceInput.vue'
import { currencySymbol } from '@/helpers/intlFormatter.js'

// Note that refund payment is different from cash refund

// todos after hotfix APP-4373
// todo: if min and max refundable is the same, don't show price input
// todo: mutate RefundableAmount in store after dispatching refund

export default {
  name: 'PaymentsTab',
  components: { PaymentsTable, BriqPriceInput },
  data() {
    return {
      showRefundDialog: false,
      paymentToRefund: null,
      refundDescription: null,
      refundMaxAmount: 0,
      refundMinAmount: 0,
    }
  },
  validations() {
    return {
      paymentToRefund: {
        Amount: {
          required,
          minValue: minValue(this.refundMinAmount || 0),
          maxValue: maxValue(this.refundMaxAmount),
        },
      },
    }
  },
  computed: {
    ...mapState('bookingDetails', ['saveError']),
    ...mapGetters('bookingDetails', [
      'payments',
      'isPending',
      'booking',
      'amountPaid',
      'externalPaymentMethods',
    ]),
    ...mapGetters('briqState', ['businessUnitHasFeature', 'userHasPermission']),
    refundError() {
      return this.$v.paymentToRefund.Amount.$error
        ? `Refund must be between ${this.formattedRefundMinAmount} and ${this.formattedRefundMaxAmount}`
        : null
    },
    disableActions() {
      return this.isPending || !this.userHasPermission(Permission.API_Admin_Booking_Payment_Create)
    },
    disableAddPayment() {
      return this.disableActions || isEmpty(this.externalPaymentMethods)
    },
    disableCashRefund() {
      return this.disableActions || this.amountPaid === 0
    },
    disableDialogRefund() {
      return !this.refundAmount || this.isPending
    },
    refundAmount: {
      get() {
        return this.paymentToRefund?.Amount
      },
      set(v) {
        if (this.paymentToRefund) {
          this.paymentToRefund.Amount = v
        }
      },
    },
    formattedRefundMinAmount() {
      return this.formatPrice(this.refundMinAmount)
    },
    formattedRefundMaxAmount() {
      return this.formatPrice(this.refundMaxAmount)
    },
    businessAllowsCashRefund() {
      return this.businessUnitHasFeature(BusinessFeature.BookingCashRefund)
    },
    businessAllowsAdminPayments() {
      return this.businessUnitHasFeature(BusinessFeature.BookingAddPayment)
    },
    currencySymbol() {
      return currencySymbol(this.$locale, this.$currency)
    },
  },
  methods: {
    formatPrice(price) {
      return currencyFormatter(this.$locale, this.$currency).format(price)
    },
    promptRefundPayment(payment) {
      this.refundMinAmount = payment.MinimumRefundAmount
      this.refundMaxAmount = payment.RefundableAmount
      this.paymentToRefund = clone(payment)
      this.paymentToRefund.Amount = this.refundMaxAmount
      this.showRefundDialog = true
    },
    async refundPayment() {
      this.$v.$touch()
      if (this.refundError) return
      this.$v.$reset()
      const dispatchModel = {
        paymentKey: this.paymentToRefund.Key,
        postModel: {
          Amount: this.paymentToRefund.Amount,
          Description: this.refundDescription,
        },
      }
      const success = await this.$store.dispatch('bookingDetails/refundPayment', dispatchModel)
      if (success) {
        this.$notifySuccess('Payment marked as refund')
        this.showRefundDialog = false
        this.paymentToRefund = null
        this.refundDescription = null
      } else this.$handleError({ error: this.saveError })
    },
  },
  beforeMount() {
    this.$setDocumentTitle(`Booking ${this.booking.Number} payments`)
  },
}
</script>
